import { HTTP_STATUS } from '@/app/config/constants';
import type { ApiError } from '@/core/error-handling/types';

/** One kind of record standing in the way of a delete, and how many of them there are. */
export interface AssociatedRecord {
  /** As the platform names it for a reader: "Product", "Site preference", "Department mapping". */
  label: string;
  count: number;
}

/** The heading above the refusal, wherever a delete is confirmed. */
export const UNABLE_TO_DELETE_TITLE = 'Unable to Delete Record';

/** What the user has to do about it. The platform says the same thing; this is the copy we lay out. */
export const REMOVE_ASSOCIATIONS_HINT =
  'Please remove all associated records or unlink this record from them before attempting to delete it.';

/** The lead-in, ending in the list of associated records. */
export const ASSOCIATED_WITH_LEAD =
  'This record cannot be deleted because it is associated with the following records:';

/**
 * The list out of the message, for a server that sent no structured detail.
 *
 * Only a fallback. The platform carries the pairs in `errors`, which `normalizeError` turns into
 * `details` -- a list built from those cannot be broken by rewording the sentence, and this one can.
 */
const LISTED = /associated with the following records:\s*([\s\S]*?)\.\s*Please remove/i;
const ENTRY = /^(.*?)\s*\((\d+)\)$/;

function fromMessage(message: string): AssociatedRecord[] {
  const listed = LISTED.exec(message)?.[1];
  if (!listed) return [];
  return listed.split(',').flatMap((part) => {
    const match = ENTRY.exec(part.trim());
    const label = match?.[1]?.trim();
    const count = Number(match?.[2]);
    return label && Number.isFinite(count) && count > 0 ? [{ label, count }] : [];
  });
}

function fromDetails(details: Record<string, string[]> | undefined): AssociatedRecord[] {
  if (!details) return [];
  return Object.entries(details).flatMap(([label, values]) => {
    const first = values[0];
    if (first === undefined) return [];
    const count = Number(first);
    return label.trim() && Number.isFinite(count) && count > 0
      ? [{ label: label.trim(), count }]
      : [];
  });
}

/**
 * Heaviest association first, then alphabetical.
 *
 * The platform orders its sentence the same way, so the record the dialog shows before "Read more" is the
 * one the sentence leads with. `details` arrives as an object, whose key order is not something to rely on.
 */
function ordered(records: AssociatedRecord[]): AssociatedRecord[] {
  return [...records].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/**
 * What is standing in the way, as a list the screen can render.
 *
 * Structured detail wins; the message is parsed only when there is none. Empty when the error is not a
 * refusal of this kind at all.
 */
export function associatedRecordsFrom(error: ApiError): AssociatedRecord[] {
  const structured = fromDetails(error.details);
  return ordered(structured.length > 0 ? structured : fromMessage(error.message));
}

/**
 * Whether this failure is "something is still attached to it" rather than anything else that can go wrong.
 *
 * Decided by the status the platform returns, not by hunting for words like "associated" in the message --
 * which used to match an unrelated 500 whose text happened to mention a reference, and would stop matching
 * the moment the refusal was reworded. A 409 that carries no list is still this kind of refusal: the
 * message is shown as it stands, and the delete is still held back.
 */
/**
 * The sentence under a "Can't delete" title: the usual lead-in when the platform itemised what is attached,
 * its own message when it did not.
 */
export function blockedLead(records: readonly AssociatedRecord[], message: string | null): string {
  return records.length > 0 ? ASSOCIATED_WITH_LEAD : (message ?? ASSOCIATED_WITH_LEAD);
}

export function isAssociationConflict(error: ApiError): boolean {
  return error.status === HTTP_STATUS.CONFLICT;
}
